import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Pencil, Loader2, Sparkles } from "lucide-react";


export default function BlogArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchArticles = async () => {
    try {
      const res = await api.get(`/blog?include_scheduled=true&limit=50`);
      setArticles(res.data.articles || []);
    } catch (err) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/blog/generate`);
      toast.success(`${res.data.total} articles generes`);
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur generation");
    } finally {
      setGenerating(false);
    }
  };

  const handleEdit = (article) => {
    setEditArticle(article);
    setEditData({ ...article });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/blog/${editArticle.id}`, editData);
      toast.success("Article mis a jour");
      setEditArticle(null);
      fetchArticles();
    } catch (err) {
      toast.error("Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateCopy = (field, value) =>
    setEditData((p) => ({ ...p, copywriting: { ...p.copywriting, [field]: value } }));

  const ANGLE_COLORS = {
    educatif: "bg-blue-100 text-blue-700",
    comparatif: "bg-purple-100 text-purple-700",
    warning: "bg-amber-100 text-amber-700",
    coût: "bg-emerald-100 text-emerald-700",
  };

  return (
    <div className="space-y-6" data-testid="blog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Articles de blog</h1>
          <p className="text-sm text-slate-500 mt-1">{articles.length} article(s)</p>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="gap-2 bg-[#336DBF] hover:bg-[#2A5A9E]"
          data-testid="generate-blog-button"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Generer les articles
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#336DBF]" />
            </div>
          ) : articles.length === 0 ? (
            <p className="text-sm text-slate-400 py-12 text-center">
              Aucun article. Cliquez sur "Generer les articles" pour creer les articles.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Titre</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Service</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Ville</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Angle</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Actif</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {articles.map((a) => (
                  <TableRow key={a.id} className="hover:bg-slate-50/50">
                    <TableCell className="text-sm font-medium text-slate-900 max-w-[300px] truncate">{a.title}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.service_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.ville_name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">{a.type_client}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={ANGLE_COLORS[a.angle] || ""}>{a.angle}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className={`w-2.5 h-2.5 rounded-full ${a.is_active ? "bg-emerald-500" : "bg-slate-300"}`} />
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={a.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                        {a.status === "published" ? "Publie" : "Programme"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(a)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editArticle} onOpenChange={() => setEditArticle(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="edit-blog-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier l'article</DialogTitle>
            <DialogDescription>{editData.title}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Titre</label>
                <Input value={editData.title || ""} onChange={(e) => setEditData((p) => ({ ...p, title: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Mot-clé principal</label>
                <Input value={editData.mot_cle_principal || ""} onChange={(e) => setEditData((p) => ({ ...p, mot_cle_principal: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Meta title</label>
                <Input value={editData.meta_title || ""} onChange={(e) => setEditData((p) => ({ ...p, meta_title: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Meta description</label>
                <Textarea value={editData.meta_description || ""} onChange={(e) => setEditData((p) => ({ ...p, meta_description: e.target.value }))} rows={2} />
              </div>
              {editData.copywriting && Object.keys(editData.copywriting).map((key) => (
                <div key={key}>
                  <label className="text-sm font-medium text-slate-700 mb-1 block capitalize">{key.replace(/_/g, " ")}</label>
                  <Textarea
                    value={editData.copywriting[key] || ""}
                    onChange={(e) => updateCopy(key, e.target.value)}
                    rows={3}
                  />
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditArticle(null)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="save-blog-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
